/**
 * useCopyToClipboard, the click handler behind every inline "Copy [X]"
 * control (a Foundation Button with the IconCopy glyph).
 *
 * Per Fix Right pattern #6: any time the UI surfaces an ID, URL, slug, or
 * other short string the user is likely to paste somewhere else, give it a
 * copy control. Saves the user a triple-click + cmd-C dance, and the toast
 * confirmation gives unambiguous "yes it copied" feedback.
 *
 * Security boundary (DO NOT BYPASS): never use this for tokens, OAuth
 * secrets, API keys, session JWTs, or any value classified as a credential.
 * Per UX-DESIGN.md "Never copy-tokens" — credentials must be re-fetched per
 * session, never sit in a clipboard buffer where another app can read them.
 *
 * Canonical example:
 *   const copy = useCopyToClipboard();
 *   <Button type="button" size="sm" onClick={() => copy(instance.id, 'Instance ID copied.')}>
 *     <IconCopy size={14} />
 *     Copy ID
 *   </Button>
 *
 * Falls back to a graceful error toast if `navigator.clipboard.writeText`
 * is unavailable or denied (e.g., HTTP origins, locked-down browsers).
 */

import { useToast } from '../components/Toast.tsx';

export function useCopyToClipboard(): (value: string, successMessage?: string) => void {
  const { showToast } = useToast();
  return (value, successMessage) => {
    navigator.clipboard.writeText(value).then(
      () => {
        showToast({ kind: 'success', message: successMessage ?? 'Copied to clipboard.' });
      },
      () => {
        showToast({
          kind: 'error',
          message: "Couldn't copy. Highlight the text and copy manually.",
        });
      },
    );
  };
}
